import React, { useEffect, useState, useRef } from "react";
import CustomerMatchBadge from "../Leads/CustomerMatchBadge";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/button";
import Skeleton from "../ui/Skeleton";
import "./CustomerPopup.css";

import InfoTab from "./ProfileTabs/InfoTab";
import CustomerOverview from "./CustomerOverview";
import AiTab from "./ProfileTabs/AiTab";
import CommunicationsTab from "./ProfileTabs/CommunicationsTab";
import TimelineTab from "./ProfileTabs/TimelineTab";
import AppointmentsTab from "./ProfileTabs/AppointmentsTab";
import EventsBookingsTab from "./ProfileTabs/EventsBookingsTab";
import WorkOrdersTab from "./ProfileTabs/WorkOrdersTab";
import InvoicesTab from "./ProfileTabs/InvoicesTab";
import CustomerCampaignsTab from "./ProfileTabs/CustomerCampaignsTab";
import RevivalTab from "./ProfileTabs/RevivalTab";
import NotesTab from "./ProfileTabs/NotesTab";
import FilesTab from "./ProfileTabs/FilesTab";
import ChemicalsTab from "./ProfileTabs/ChemicalsTab";
import ScheduleModal from "../Customers/Modals/ScheduleModal";
import StatusModal from "../Customers/Modals/StatusModal";
import LeadActionBar from "../Leads/LeadActionBar";
import PopupTabBoundary from "./PopupTabBoundary";
import AtRiskBanner from "../Customers/AtRisk/AtRiskBanner";
import { buildCustomerPatch } from "./customerPatch";

import { mapEntity } from "../../utils/contactMapper";
import { formatPhone, money, plainWords, telHref } from "./customerFormat";
import api from "../../apiClient";
import { getIndustryKey, getSchedulingConfig } from "../../constants/uiRegistry";

/* ============================================================
   MAIN POPUP
============================================================ */
// Surface an API error as readable text — a string detail/error as-is, and a
// DRF field-error object spelled out ("address: This field is required.")
// rather than the "[object Object]" a raw object renders as.
const errText = (err, fallback = "Request failed.") => {
  const d = err?.response?.data;
  if (typeof d === "string" && d.trim()) return d.trim();
  if (d && typeof d === "object") {
    if (typeof d.detail === "string" && d.detail) return d.detail;
    if (typeof d.error === "string" && d.error) return d.error;
    const spelled = Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : typeof v === "object" ? JSON.stringify(v) : String(v)}`)
      .join("  •  ");
    if (spelled) return spelled;
  }
  return err?.message || fallback;
};

// The window header's one row of facts, after the name and status badge:
// account no · FR id · phone (tap to call) + Text · email · balance (tabular).
// Address, next service and the rest live in the Overview columns.
function CustomerHeadline({ raw, fr, onText }) {
  const account = String(raw.account_number || "").trim();
  const frId = fr?.customer_id || raw.fieldroutes_customer_id || "";
  const phone = raw.primary_phone || raw.phone || raw.secondary_phone || "";
  const email = raw.primary_email || raw.email || raw.secondary_email || "";
  const balance = fr ? Number(fr.balance) : null;
  const parts = [];
  const ids = [account, frId ? `FR ${frId}` : ""].filter(Boolean).join(" · ");
  if (ids) parts.push(<span key="ids" className="cp-meta__id">{ids}</span>);
  if (phone) {
    parts.push(
      <a key="phone" href={telHref(phone)} title="Call">
        {formatPhone(phone)}
      </a>
    );
    if (onText)
      parts.push(
        <button key="text" type="button" className="cp-meta__text" onClick={onText} title="Text this customer">
          Text
        </button>
      );
  }
  if (email) parts.push(<a key="email" href={`mailto:${email}`}>{email}</a>);
  if (balance != null && Number.isFinite(balance))
    parts.push(
      <span key="bal" className={`cp-meta__bal${balance > 0 ? " cp-meta__bal--warn" : ""}`}>
        Balance {money(balance, { cents: true })}
      </span>
    );
  return parts.length ? <span className="cp-meta">{parts}</span> : null;
}

/* `initialTab` lets a caller open the card straight onto a tab — used by the
   New Quote entry, which has already decided you want Quotes. Optional and
   defaulted, so every existing caller is unchanged. */
function CustomerPopupInternal({ lead, onClose, initialTab, openComposer, focusAppointment = null, boardStartLabel = null }) {
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [preferences, setPreferences] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  // "+ New Quote" from the events Bookings tab hands off to the Quotes tab and
  // opens its composer directly (openComposer is a prop; this is the local lever).
  const [forceCompose, setForceCompose] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const industryKey = getIndustryKey("general");
  const schedulingConfig = getSchedulingConfig(industryKey);

  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const chatRef = useRef(null);

  /* ---------------------------------------------------------
     LOCK BODY SCROLL
  --------------------------------------------------------- */
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  useEffect(() => {
    if (!record) return;
    setFormData(record.raw || {});
  }, [record]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/accounts/preferences/");
        if (mounted) setPreferences(data?.preferences || {});
      } catch {
        if (mounted) setPreferences(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------------------------------------------------------
     UNIVERSAL CUSTOMER RESOLUTION (THE FIX)
  --------------------------------------------------------- */
  useEffect(() => {
    if (!lead) return;

    if (lead?.object && lead?.id == null && lead?.customer_id == null) {
      setRecord(mapEntity(lead));
      return;
    }
  
    async function resolveIdentity() {
      try {
        // STEP 1 — always map first (lead/revival/customer)
        const mapped = mapEntity(lead);
  
        // If already a complete customer → done. "Complete" means the DETAIL
        // record, not a list row: list serializers carry phones/city/state but
        // not first_name/address, and trusting them here rendered cards with a
        // populated header and empty name/address boxes (the Dekouski bug —
        // zero detail fetches ever fired). Detail markers must be present.
        const rawRec = mapped.raw || {};
        // `service_plans` only exists on the detail payload (with the FieldRoutes summary).
        const hasDetailFields = "first_name" in rawRec && "address" in rawRec && "service_plans" in rawRec;
        if (mapped.object === "customer" && mapped.phones?.length > 0 && hasDetailFields) {
          setRecord(mapped);
          return;
        }
  
        // STEP 2 — Universal ID direct resolution. Only for a REAL uid; a record
        // with no uid (or a leftover fabricated temp-… one) skips this lookup
        // instead of 404-ing on /customers/by-uid/temp-….
        if (mapped.universal_id && !String(mapped.universal_id).startsWith("temp-")) {
          try {
            const { data } = await api.get(
              `/customers/by-uid/${mapped.universal_id}/`
            );
            if (data) {
              // 🔥 Merge mapped lead/revival info so mapper can combine phones/emails
              // Tag as customer — this came from /api/customers/, so it IS a customer.
              setRecord(
                mapEntity({
                  ...data,
                  object: "customer",
                  lead: mapped.raw,
                })
              );
              return;
            }
          } catch {}
        }

        // STEP 2.5 — Customer detail fetch by id (list rows without a
        // universal_id still need the full record; PK or customer_id UUID
        // both resolve against /customers/{id}/).
        if (mapped.object === "customer" && (mapped.id || mapped.raw?.id)) {
          try {
            const { data } = await api.get(`/customers/${mapped.raw?.id || mapped.id}/`);
            if (data) {
              setRecord(mapEntity({ ...data, object: "customer", lead: mapped.raw }));
              return;
            }
          } catch {}
        }

        // STEP 3 — Revival fallback using customer_id
        if (mapped.object === "revival" && mapped.customer_id) {
          try {
            const { data } = await api.get(
              `/customers/${mapped.customer_id}/`
            );
            if (data) {
              // 🔥 Merge revival + customer fully
              // Tag as customer — this came from /api/customers/, so it IS a customer.
              setRecord(
                mapEntity({
                  ...data,
                  object: "customer",
                  lead: mapped.raw, // revival identity passes through mapper
                })
              );
              return;
            }
          } catch {}
        }
  
        // STEP 4 — Lead fallback (no customer yet)
        if (mapped.object === "lead") {
          setRecord(mapped);
          return;
        }
  
        // STEP 5 — Unknown fallback
        setRecord(mapEntity(mapped.raw || mapped));
      } catch (err) {
        console.error("Popup load error:", err);
        toast.error("Failed loading profile");
  
        // 🔥 Emergency fallback
        setRecord(mapEntity(lead));
      }
    }
  
    resolveIdentity();
  }, [lead, refreshTick]);
  
  
  /* ---------------------------------------------------------
     LOADING GATE
  --------------------------------------------------------- */
  if (!record) {
    return (
      <Modal open onClose={onClose} variant="window" windowKey="customer" className="customer-popup-wrapper" title="Loading…">
        <Skeleton variant="card" count={2} />
      </Modal>
    );
  }

  /* ---------------------------------------------------------
     API PATHS
     Lead IDs vs Customer IDs are distinct keyspaces. Leads use the
     CRMLead PK (numeric) against /leads/crm-leads/{id}/. Customers use
     either their numeric PK or the customer_id UUID against /customers/{id}/.
     Web-inbox leads that haven't been forwarded to the CRM yet have
     no CRMLead PK — save is blocked until they're added to the pipeline.
  --------------------------------------------------------- */
  const leadUpdateId = record.crm_lead_id || record.raw?.crm_lead_id || record.id;
  const customerUpdateId = record.raw?.customer_id || record.id;

  // Derive the entity kind STRUCTURALLY from the record, not from record.object
  // alone — a list-click can reach this popup without that flag stamped, and a
  // lead that then falls through to the customer branch opens the wrong (worse)
  // view and PATCHes /customers/{lead_id}/. Priority mirrors the mapper:
  // revival → lead → customer, with customer as the fallback. A record is a
  // lead when it carries a lead identity and is NOT a resolved customer (real
  // customer UUID / account number); that positive test survives a missing flag.
  const rawRec = record.raw || {};
  const isCustomerUUID = (v) => typeof v === "string" && /^[0-9a-f-]{32,36}$/i.test(v);
  const isRevival =
    record.object === "revival" ||
    rawRec.estimated_total !== undefined ||
    Array.isArray(rawRec.items) ||
    rawRec.public_token !== undefined;
  const isResolvedCustomer =
    record.object === "customer" &&
    (isCustomerUUID(record.id) ||
      isCustomerUUID(rawRec.customer_id) ||
      rawRec.account_number !== undefined);
  const hasLeadIdentity =
    record.object === "lead" ||
    record.crm_lead_id != null ||
    rawRec.crm_lead_id != null ||
    record.pipeline_stage != null ||
    rawRec.pipeline_stage != null ||
    rawRec.lead_number != null ||
    rawRec.source_table === "forms_generic" ||
    rawRec.intake_attributes !== undefined;
  const isLead = !isRevival && hasLeadIdentity && !isResolvedCustomer;
  const isCustomer = !isRevival && !isLead;

  const updatePaths = {
    lead: `/leads/crm-leads/${leadUpdateId}/`,
    customer: `/customers/${customerUpdateId}/`,
    revival: `/revival/${record.id}/`,
  };

  const updateUrl = isLead
    ? updatePaths.lead
    : isRevival
    ? updatePaths.revival
    : updatePaths.customer;

  const convertUrl = isLead
    ? `/leads/crm-leads/${leadUpdateId}/convert-to-customer/`
    : isRevival
    ? `/revival/${record.id}/convert/`
    : null;
  const isCreate = !record.id;
  const isPendingWebLead = Boolean(record.is_pending_web_lead);
  const canUpdateStatus = (isLead || isRevival) && !isCreate;
  const canSchedule = !!record.id;
  const displayIndustry = record.raw?.industry || record.industry || "general";

  const updateProfileField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildLeadPayload = (raw = {}) => {
    const payload = {};
    const name = raw.name || raw.full_name;
    const primaryEmail = raw.primary_email ?? raw.email;
    const primaryPhone = raw.primary_phone ?? raw.phone_number;
    const serviceType = raw.service_type ?? raw.service;
    const leadSource = raw.lead_source ?? raw.source_host;
    const notes = raw.notes ?? raw.message;

    if (raw.first_name !== undefined) payload.first_name = raw.first_name;
    if (raw.last_name !== undefined) payload.last_name = raw.last_name;
    if (name !== undefined) payload.name = name;

    if (primaryEmail !== undefined) payload.primary_email = primaryEmail;
    if (primaryPhone !== undefined) payload.primary_phone = primaryPhone;

    if (raw.email !== undefined || primaryEmail !== undefined) {
      payload.email = raw.email ?? primaryEmail ?? "";
    }
    if (raw.phone_number !== undefined || primaryPhone !== undefined) {
      payload.phone_number = raw.phone_number ?? primaryPhone ?? "";
    }

    if (raw.industry !== undefined || record.industry) {
      payload.industry = raw.industry || record.industry || "general";
    }
    if (raw.status !== undefined) payload.status = raw.status;

    if (serviceType !== undefined) payload.service = serviceType;
    if (notes !== undefined) payload.message = notes;
    if (raw.industry_role !== undefined) payload.industry_role = raw.industry_role;
    if (raw.company_name !== undefined) payload.company_name = raw.company_name;
    if (raw.is_business !== undefined) payload.is_business = raw.is_business;
    if (raw.address !== undefined) payload.address = raw.address;
    if (raw.attributes != null) payload.attributes = raw.attributes;
    if (raw.intake_attributes != null) payload.intake_attributes = raw.intake_attributes;
    if (leadSource !== undefined) payload.source_host = leadSource;
    if (raw.recaptcha_score !== undefined) payload.recaptcha_score = raw.recaptcha_score;

    const offering = raw.offering?.id ?? raw.offering;
    if (offering) payload.offering = offering;

    if (raw.priority_score != null) {
      const score = Number(raw.priority_score);
      payload.priority_score = Number.isFinite(score) ? score : raw.priority_score;
    }
    if (raw.summary != null) payload.summary = raw.summary;
    if (raw.tags != null) payload.tags = raw.tags;

    const allowed = new Set([
      "name",
      "first_name",
      "last_name",
      "email",
      "phone_number",
      "primary_email",
      "primary_phone",
      "service",
      "service_type",
      "lead_source",
      "message",
      "notes",
      "industry",
      "industry_role",
      "company_name",
      "is_business",
      "address",
      "attributes",
      "intake_attributes",
      "source_host",
      "recaptcha_score",
      "offering",
      "priority_score",
      "summary",
      "status",
      "tags",
    ]);

    const attributePatch = {};
    Object.entries(formData || {}).forEach(([key, val]) => {
      if (val === undefined || String(val).trim() === "") return;
      if (allowed.has(key)) {
        if (payload[key] === undefined) payload[key] = val;
      } else {
        attributePatch[key] = val;
      }
    });
    if (raw.service_type !== undefined && payload.service === undefined) {
      payload.service = raw.service_type;
    }
    if (raw.lead_source !== undefined && payload.source_host === undefined) {
      payload.source_host = raw.lead_source;
    }
    if (raw.notes !== undefined && payload.message === undefined) {
      payload.message = raw.notes;
    }

    if (
      payload.service !== undefined ||
      payload.source_host !== undefined ||
      payload.message !== undefined ||
      Object.keys(attributePatch).length > 0
    ) {
      const baseAttributes = raw.attributes && typeof raw.attributes === "object" ? raw.attributes : {};
      const leadMetadata = {
        ...(payload.service !== undefined ? { service_type: payload.service } : {}),
        ...(payload.source_host !== undefined ? { lead_source: payload.source_host } : {}),
        ...(payload.message !== undefined ? { notes: payload.message } : {}),
      };
      payload.attributes = {
        ...baseAttributes,
        ...leadMetadata,
        ...attributePatch,
      };
      payload.intake_attributes = {
        ...(raw.intake_attributes && typeof raw.intake_attributes === "object" ? raw.intake_attributes : {}),
        ...leadMetadata,
      };
    }

    const cleaned = Object.fromEntries(
      Object.entries(payload).filter(
        ([, v]) => v !== undefined && v !== null && String(v).trim() !== ""
      )
    );
    return cleaned;
  };

  // CREATE: everything the user set (diffed against empty), plus the create-only
  // fallbacks. Still no snapshot — unknown keys are never swept in.
  const buildCustomerCreate = () => {
    const payload = buildCustomerPatch(formData, {});
    const raw = record?.raw || {};
    if (!payload.industry && record.industry) payload.industry = record.industry;
    if (!payload.primary_phone && raw.phone_number) payload.primary_phone = raw.phone_number;
    if (!payload.primary_email && raw.email) payload.primary_email = raw.email;
    return Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
    );
  };

  /* ---------------------------------------------------------
     SAVE PROFILE
  --------------------------------------------------------- */
  const handleSaveChanges = async () => {
    try {
      const workingData = { ...(record?.raw || {}), ...formData };
      const hasPhone =
        workingData.phone_number ||
        workingData.primary_phone ||
        workingData.secondary_phone;
      const hasEmail =
        workingData.email ||
        workingData.primary_email ||
        workingData.secondary_email;
      if (isCreate) {
        if (isLead) {
          const leadName = (workingData.name || `${workingData.first_name || ""} ${workingData.last_name || ""}`.trim() || "").trim();
          const required = [
            { key: "name", value: leadName },
            { key: "phone", value: workingData.primary_phone || workingData.phone_number },
            { key: "email", value: workingData.primary_email || workingData.email },
            { key: "address", value: workingData.address },
            { key: "service_type", value: workingData.service_type || workingData.service },
            { key: "lead_source", value: workingData.lead_source || workingData.source_host },
            { key: "notes", value: workingData.notes || workingData.message },
          ];
          const missing = required
            .filter((item) => !String(item.value || "").trim())
            .map((item) => item.key);
          if (missing.length > 0) {
            toast.error(`Complete required lead fields: ${missing.join(", ")}`);
            return;
          }
        } else {
          if (!workingData.first_name && !workingData.name) {
            toast.error("First name is required.");
            return;
          }
          if (!workingData.last_name) {
            toast.error("Last name is required.");
            return;
          }
          if (!hasPhone && !hasEmail) {
            toast.error("Provide at least one phone or email.");
            return;
          }
          if (!workingData.address) {
            toast.error("Address is required.");
            return;
          }
        }
      }
      let serverData = null;
      if (isLead) {
        const payload = buildLeadPayload(workingData);
        if (!payload.name) {
          payload.name = [payload.first_name, payload.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        }
        if (isCreate) {
          const { data } = await api.post("/leads/crm-leads/", payload);
          setRecord(mapEntity(data));
          setFormData(data || {});
          toast.success("Lead created.");
          return;
        }
        if (isPendingWebLead) {
          // Web inbox row that has NOT been forwarded to CRM yet — no CRMLead to PATCH.
          // Create a new CRMLead now, carrying the web lead metadata so the pipeline
          // picks it up, then mark the web lead as handled.
          const { data } = await api.post("/leads/crm-leads/", {
            ...payload,
            source_host: record.raw?.source_host,
            source_table: "forms_generic",
            industry: record.raw?.industry || record.industry || "general",
          });
          if (record.web_lead_id) {
            try {
              await api.post(`/leads/web/${record.web_lead_id}/archive/`);
            } catch {
              // Non-fatal: the CRMLead is created; web row archive can be retried.
            }
          }
          setRecord(mapEntity(data));
          setFormData(data || {});
          toast.success("Lead added to pipeline.");
          return;
        }
        const { data } = await api.patch(updateUrl, payload);
        serverData = data;
      } else if (isCustomer) {
        if (isCreate) {
          const { data } = await api.post("/customers/", buildCustomerCreate());
          setRecord(mapEntity(data));
          setFormData(data || {});
          toast.success("Customer created.");
          return;
        }
        // UPDATE: only the fields the user changed — never a record snapshot.
        const patch = buildCustomerPatch(formData, record?.raw || {});
        if (Object.keys(patch).length === 0) {
          toast.info("No changes to save.");
          return;
        }
        const { data } = await api.patch(updateUrl, patch);
        serverData = data;
      } else {
        const { data } = await api.patch(updateUrl, record.raw);
        serverData = data;
      }

      const nextRaw = { ...(record?.raw || {}), ...workingData, ...(serverData || {}) };
      const nextFullName =
        nextRaw.full_name ||
        nextRaw.name ||
        `${nextRaw.first_name || ""} ${nextRaw.last_name || ""}`.trim() ||
        record.full_name;

      setRecord((prev) =>
        prev
          ? {
              ...prev,
              full_name: nextFullName,
              industry: nextRaw.industry || prev.industry,
              raw: nextRaw,
            }
          : prev
      );
      setFormData(nextRaw);
      toast.success("Saved.");
    } catch (err) {
      toast.error(errText(err, isCreate ? "Create failed." : "Save failed."));
    }
  };

  /* ---------------------------------------------------------
     LEAD → CUSTOMER CONVERSION
  --------------------------------------------------------- */
  const handleConvertToCustomer = async () => {
    if (!convertUrl) return;

    try {
      const { data } = await api.post(convertUrl, {});
      const nextId =
        data?.customer_id ||
        data?.customer?.id ||
        data?.customer ||
        data?.id;
      if (!nextId) throw new Error("Missing customer id");

      const full = await api.get(`/customers/${nextId}/`);
      setRecord(mapEntity({ ...full.data, object: "customer" }));
      toast.success("Converted.");
    } catch (err) {
      toast.error(errText(err, "Failed to convert."));
    }
  };

  /* ---------------------------------------------------------
     STATUS UPDATE
  --------------------------------------------------------- */
  const STATUS_OPTIONS = isRevival
    ? ["draft", "sent", "accepted", "rejected", "converted", "completed"]
    : ["new", "contacted", "qualified", "scheduled", "closed", "dead"];

  const updateStatus = async (status) => {
    try {
      if (!canUpdateStatus) return;

      await api.patch(updateUrl, { status });

      setRecord((prev) => ({
        ...prev,
        raw: {
          ...prev.raw,
          status,
        },
      }));

      toast.success("Status updated");
      setShowStatusModal(false);
    } catch (err) {
      toast.error(errText(err, "Failed to update status."));
    }
  };

  /* ---------------------------------------------------------
     TABS
  --------------------------------------------------------- */
  const renderTab = () => {
    // Pass the DERIVED kind down, not the possibly-absent record.object, so
    // child tabs classify the same way this popup does.
    const derivedObject = isLead ? "lead" : isRevival ? "revival" : "customer";
    const raw = { ...record.raw, object: derivedObject, industry: record.industry };

    const onOverviewChange = (e) => {
      const { name, value } = e.target;
      updateProfileField(name, value);
    };

    switch (activeTab) {
      case "overview":
        // Customers get the full property record (Street View hero, stat tiles,
        // Plans / Appointments / Quotes / Access & Safety, plus the editable
        // detail form). Leads and quotes keep the plain InfoTab.
        if (isCustomer) {
          return (
            <CustomerOverview
              record={record}
              formData={formData}
              preferences={preferences}
              onChange={onOverviewChange}
            />
          );
        }
        return (
          <InfoTab
            lead={raw}
            formData={formData}
            preferences={preferences}
            onChange={onOverviewChange}
          />
        );
      case "timeline":
        return <TimelineTab record={record} />;
      case "appointments":
        // Events tenants get the event-specific Bookings tab (venue / package /
        // deposit·balance / contract); every other industry keeps the generic
        // appointments list.
        if (industryKey === "events") {
          return (
            <EventsBookingsTab
              record={record}
              onNewQuote={() => {
                setForceCompose(true);
                setActiveTab("revival");
              }}
            />
          );
        }
        return <AppointmentsTab record={record} focusAppointment={focusAppointment} boardStartLabel={boardStartLabel} />;
      case "work_orders":
        return <WorkOrdersTab record={record} />;
      case "chemicals":
        return <ChemicalsTab record={record} />;
      case "communications":
        return <CommunicationsTab lead={record} />;
      case "invoices":
        return <InvoicesTab record={record} />;
      case "campaigns":
        return <CustomerCampaignsTab record={record} />;
      case "revival":
        return <RevivalTab customer={record} autoCompose={openComposer || forceCompose} />;
      case "ai_insights":
        return (
          <AiTab
            lead={record}
            aiMessages={aiMessages}
            aiInput={aiInput}
            setAiInput={setAiInput}
            setAiMessages={setAiMessages}
            chatRef={chatRef}
          />
        );
      case "notes":
        return (
          <NotesTab
            record={record}
            formData={formData}
            onFieldChange={updateProfileField}
          />
        );
      case "files":
        return <FilesTab record={record} />;
      default:
        return (
          <div className="cp-tab-empty">
            <p>Choose a tab above to see this customer’s details.</p>
          </div>
        );
    }
  };

  /* ---------------------------------------------------------
     JSX
  --------------------------------------------------------- */
  // Tabs are GATED BY INDUSTRY — no vertical bleed. A pest-only tab (Chemicals)
  // never shows on an events or general card; events uses its own vocabulary
  // (Bookings, not Appointments). Each set is explicit rather than a shared
  // superset with holes, so adding a vertical can't accidentally inherit
  // another's tabs. Unknown industries fall back to `generalTabs`.
  const eventsTabs = [
    { id: "overview", label: "Overview" },
    { id: "timeline", label: "Timeline" },
    { id: "appointments", label: "Bookings" },
    { id: "communications", label: "Communications" },
    { id: "revival", label: "Quotes" },
    { id: "files", label: "Documents" },
    { id: "notes", label: "Notes" },
    { id: "ai_insights", label: "AI insights" },
  ];

  const pestTabs = [
    { id: "overview", label: "Overview" },
    { id: "timeline", label: "Timeline" },
    { id: "appointments", label: "Appointments" },
    { id: "chemicals", label: "Chemicals" },
    { id: "work_orders", label: "Work orders" },
    { id: "communications", label: "Communications" },
    { id: "invoices", label: "Invoices" },
    { id: "campaigns", label: "Campaigns" },
    { id: "revival", label: "Quotes" },
    { id: "files", label: "Documents" },
    { id: "notes", label: "Notes" },
    { id: "ai_insights", label: "AI insights" },
  ];

  // General / landscaping / other field service: no Chemicals (pest concept),
  // but keep work orders, invoices and campaigns.
  const generalTabs = [
    { id: "overview", label: "Overview" },
    { id: "timeline", label: "Timeline" },
    { id: "appointments", label: "Appointments" },
    { id: "work_orders", label: "Work orders" },
    { id: "communications", label: "Communications" },
    { id: "invoices", label: "Invoices" },
    { id: "campaigns", label: "Campaigns" },
    { id: "revival", label: "Quotes" },
    { id: "files", label: "Documents" },
    { id: "notes", label: "Notes" },
    { id: "ai_insights", label: "AI insights" },
  ];

  const tabs =
    industryKey === "events"
      ? eventsTabs
      : industryKey === "pest_control"
      ? pestTabs
      : generalTabs;
  const frSummary = record.raw?.fieldroutes || null;
  // commercial FR accounts carry the company name in first_name; show it once, not twice
  const companyName = String(record.raw?.company_name || frSummary?.company_name || "").trim();
  const nameLabel =
    (isCustomer && frSummary?.commercial && companyName) ||
    record.full_name ||
    record.raw?.name ||
    (isLead ? "New Lead" : isRevival ? "Quote" : "New Customer");
  const typeLabel = isLead ? "Lead" : isRevival ? "Quote" : "Customer";
  const statusLabel = record.raw?.status || record.status || "";
  const customerStatus =
    frSummary?.status ||
    (record.raw?.is_active === false ? "Inactive" : "") ||
    record.raw?.sales_status ||
    (record.raw?.is_active === true ? "Active" : "");
  const customerHeadline = isCustomer && !isCreate ? (
    <CustomerHeadline raw={record.raw || {}} fr={frSummary} onText={() => setActiveTab("communications")} />
  ) : null;

  const modalTabs = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    active: activeTab === tab.id,
    onClick: () => setActiveTab(tab.id),
  }));

  const openNewQuote = () => {
    onClose?.();
    navigate("/quotes/create", {
      state: {
        customer: {
          id: record?.customer_id || record?.id,
          customer_id: record?.customer_id || record?.id,
          first_name: record?.first_name || "",
          last_name: record?.last_name || "",
          full_name:
            record?.full_name ||
            record?.raw?.name ||
            [record?.first_name, record?.last_name].filter(Boolean).join(" "),
          company_name: record?.company_name || "",
          primary_phone: record?.primary_phone || record?.phone || record?.phone_number || "",
          primary_email: record?.primary_email || record?.email || "",
          service_type: record?.service_type || record?.service || "",
          message: record?.message || record?.notes || "",
        },
      },
    });
  };

  // Header actions on desktop, the bottom dock on a phone. One primary: Save.
  const actions = (
    <>
      {isLead && convertUrl && !isCreate && (
        <Button size="sm" onClick={handleConvertToCustomer}>
          Convert
        </Button>
      )}
      {canUpdateStatus && (
        <Button size="sm" onClick={() => setShowStatusModal(true)}>
          Status
        </Button>
      )}
      <Button size="sm" onClick={openNewQuote} title="Create a new quote for this contact">
        New quote
      </Button>
      <Button
        size="sm"
        onClick={() => setShowScheduleModal(true)}
        disabled={!canSchedule}
        title={!canSchedule ? "Save first to schedule" : undefined}
      >
        Schedule
      </Button>
      <Button size="sm" variant="primary" onClick={handleSaveChanges}>
        {isCreate ? "Create" : "Save"}
      </Button>
    </>
  );

  return (
    <>
      <Modal
        open
        onClose={onClose}
        variant="window"
        windowKey="customer"
        className="customer-popup-wrapper"
        title={nameLabel}
        meta={
          customerHeadline ||
          `${typeLabel} · ${plainWords(displayIndustry)}${statusLabel && isCustomer ? ` · ${plainWords(statusLabel)}` : ""}`
        }
        status={
          isCustomer
            ? plainWords(customerStatus) || undefined
            : statusLabel
            ? plainWords(statusLabel)
            : undefined
        }
        tabs={modalTabs}
        actions={actions}
        banner={isCustomer && !isCreate ? (
          <AtRiskBanner
            customerId={record.raw?.id || record.customer_id || record.id}
            onAction={(kind, issue) => {
              const phone = issue?.action?.phone || record.raw?.primary_phone || record.raw?.phone;
              if (kind === "call" && phone) { window.location.href = telHref(phone); return; }
              setActiveTab(kind === "text" ? "communications" : "ai_insights");
            }}
          />
        ) : isLead && !isCreate ? (
          <CustomerMatchBadge lead={record.raw || record} className="customer-popup-match" />
        ) : null}
      >
        {isLead && !isCreate && (
          <LeadActionBar
            leadId={leadUpdateId}
            record={record}
            onUpdated={() => setRefreshTick((t) => t + 1)}
          />
        )}
        <PopupTabBoundary tabKey={activeTab}>{renderTab()}</PopupTabBoundary>
      </Modal>

      {/* STATUS MODAL */}
      {showStatusModal && canUpdateStatus && (
        <StatusModal
          options={STATUS_OPTIONS}
          onSelect={updateStatus}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      {showScheduleModal && record && (
        <ScheduleModal
          record={record}
          schedulingConfig={schedulingConfig}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </>
  );
}

/* ============================================================
   EXPORT — Modal owns the portal into #modal-root now.
============================================================ */
export default function CustomerPopup(props) {
  return <CustomerPopupInternal {...props} />;
}
